import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  Play,
  RefreshCw,
  Send,
  Sparkles,
  XCircle,
} from 'lucide-react';
import api from '../lib/api';
import { Job, Organization } from '../types';
import Button from './ui/Button';
import Badge from './ui/Badge';
import Select from './ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';

type PlanMode = 'campaign_launch' | 'workflow_rescue' | 'shortlist_broadcast';
type ApprovalStatus = 'PROPOSED' | 'APPROVED' | 'REJECTED';
type ExecutionStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'SKIPPED';

type AIActionItem = {
  id: string;
  clientActionId: string;
  type: string;
  title: string;
  description: string;
  params: Record<string, unknown>;
  editableFields: string[];
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  approvalStatus: ApprovalStatus;
  executionStatus: ExecutionStatus;
  dryRunResult?: { ok?: boolean; warnings?: string[]; errors?: string[] } | null;
  executionResult?: Record<string, unknown> | null;
  errorMessage?: string | null;
};

type AIActionPlan = {
  id: string;
  title: string;
  summary: string;
  mode: PlanMode;
  status: string;
  model?: string | null;
  plannerLatencyMs?: number | null;
  actions: AIActionItem[];
  createdAt: string;
};

const starterPrompts: Record<PlanMode, string> = {
  campaign_launch: 'Create a TikTok launch campaign with 20 beauty KOLs and a two-week review timeline.',
  workflow_rescue: 'What needs attention this week? Prepare reminder drafts for stalled campaign work.',
  shortlist_broadcast: 'Find the best matched KOLs for this campaign and prepare invitation records for approval.',
};

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  if (status === 'DONE' || status === 'COMPLETED' || status === 'APPROVED') return 'success';
  if (status === 'FAILED' || status === 'REJECTED') return 'destructive';
  if (status === 'RUNNING' || status === 'PARTIAL') return 'warning';
  if (status === 'SKIPPED') return 'secondary';
  return 'outline';
}

function riskVariant(risk: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  if (risk === 'HIGH') return 'destructive';
  if (risk === 'MEDIUM') return 'warning';
  return 'success';
}

function jsonText(value: unknown) {
  return JSON.stringify(value ?? {}, null, 2);
}

export default function AIActionPlanPanel() {
  const [mode, setMode] = useState<PlanMode>('campaign_launch');
  const [prompt, setPrompt] = useState(starterPrompts.campaign_launch);
  const [brandOrgId, setBrandOrgId] = useState('');
  const [jobId, setJobId] = useState('');
  const [brands, setBrands] = useState<Organization[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [plans, setPlans] = useState<AIActionPlan[]>([]);
  const [activePlan, setActivePlan] = useState<AIActionPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editBuffers, setEditBuffers] = useState<Record<string, string>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    void loadInitial();
  }, []);

  useEffect(() => {
    setPrompt(starterPrompts[mode]);
  }, [mode]);

  useEffect(() => {
    if (!activePlan) return;
    const next: Record<string, string> = {};
    for (const action of activePlan.actions) next[action.id] = jsonText(action.params);
    setEditBuffers(next);
    setEditErrors({});
  }, [activePlan?.id]);

  const approvedCount = useMemo(
    () => activePlan?.actions.filter((a) => a.approvalStatus === 'APPROVED').length ?? 0,
    [activePlan]
  );

  async function loadInitial() {
    setLoading(true);
    setError(null);
    try {
      const [orgRes, jobsRes, planRes] = await Promise.all([
        api.get<Organization[]>('/organizations'),
        api.get<Job[]>('/jobs'),
        api.get<AIActionPlan[]>('/ai-action-plans'),
      ]);
      const brandOrgs = orgRes.data.filter((org) => org.type === 'BRAND');
      setBrands(brandOrgs);
      setJobs(jobsRes.data);
      if (!brandOrgId && brandOrgs[0]) setBrandOrgId(brandOrgs[0].id);
      if (!jobId && jobsRes.data[0]) setJobId(jobsRes.data[0].id);
      setPlans(planRes.data);
      setActivePlan(planRes.data[0] ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load AI action plans');
    } finally {
      setLoading(false);
    }
  }

  async function refreshPlan(planId = activePlan?.id) {
    if (!planId) return;
    const res = await api.get<AIActionPlan>(`/ai-action-plans/${planId}`);
    setActivePlan(res.data);
    setPlans((prev) => [res.data, ...prev.filter((p) => p.id !== res.data.id)]);
  }

  async function createPlan() {
    setBusyLabel('Planning');
    setError(null);
    try {
      const res = await api.post<AIActionPlan>('/ai-action-plans/plan', {
        mode,
        prompt,
        brandOrgId: mode === 'campaign_launch' ? brandOrgId : undefined,
        jobId: mode !== 'campaign_launch' ? jobId : undefined,
      });
      setActivePlan(res.data);
      setPlans((prev) => [res.data, ...prev.filter((p) => p.id !== res.data.id)]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to create action plan');
    } finally {
      setBusyLabel(null);
    }
  }

  async function dryRun() {
    if (!activePlan) return;
    setBusyLabel('Dry run');
    setError(null);
    try {
      await api.post(`/ai-action-plans/${activePlan.id}/dry-run`);
      await refreshPlan(activePlan.id);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Dry run failed');
    } finally {
      setBusyLabel(null);
    }
  }

  async function approveAll() {
    if (!activePlan) return;
    setBusyLabel('Approving');
    setError(null);
    try {
      const res = await api.post<AIActionPlan>(`/ai-action-plans/${activePlan.id}/approve`);
      setActivePlan(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Approval failed');
    } finally {
      setBusyLabel(null);
    }
  }

  async function execute() {
    if (!activePlan) return;
    setBusyLabel('Executing');
    setError(null);
    try {
      const res = await api.post<{ plan: AIActionPlan }>(`/ai-action-plans/${activePlan.id}/execute`);
      setActivePlan(res.data.plan);
      setPlans((prev) => [res.data.plan, ...prev.filter((p) => p.id !== res.data.plan.id)]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Execution failed');
      await refreshPlan(activePlan.id);
    } finally {
      setBusyLabel(null);
    }
  }

  async function saveAction(action: AIActionItem) {
    if (!activePlan) return;
    setBusyLabel('Saving');
    setError(null);
    try {
      const parsed = JSON.parse(editBuffers[action.id] || '{}');
      setEditErrors((prev) => ({ ...prev, [action.id]: '' }));
      const res = await api.patch<AIActionPlan>(`/ai-action-plans/${activePlan.id}/actions/${action.id}`, {
        params: parsed,
      });
      setActivePlan(res.data);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setEditErrors((prev) => ({ ...prev, [action.id]: 'Invalid JSON' }));
      } else {
        setError(err?.response?.data?.error || 'Failed to save action');
      }
    } finally {
      setBusyLabel(null);
    }
  }

  async function rejectAction(actionId: string) {
    if (!activePlan) return;
    setBusyLabel('Skipping');
    setError(null);
    try {
      const res = await api.post<AIActionPlan>(`/ai-action-plans/${activePlan.id}/actions/${actionId}/reject`);
      setActivePlan(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to skip action');
    } finally {
      setBusyLabel(null);
    }
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent2" />
                AI Action Plans
              </CardTitle>
              <p className="mt-1 text-sm text-glass-text-2">
                Generate editable campaign actions, approve them, then execute with audited backend tools.
              </p>
            </div>
            <Button onClick={() => void loadInitial()} variant="outline" disabled={loading || !!busyLabel}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[180px_220px_240px_1fr_auto]">
            <Select value={mode} onChange={(e) => setMode(e.target.value as PlanMode)}>
              <option value="campaign_launch">Campaign launch</option>
              <option value="workflow_rescue">Workflow rescue</option>
              <option value="shortlist_broadcast">KOL shortlist</option>
            </Select>
            <Select
              value={brandOrgId}
              onChange={(e) => setBrandOrgId(e.target.value)}
              disabled={mode !== 'campaign_launch'}
            >
              <option value="">Select brand</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </Select>
            <Select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              disabled={mode === 'campaign_launch'}
            >
              <option value="">Select campaign</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>{job.title}</option>
              ))}
            </Select>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              className="min-h-[86px] w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
            />
            <Button data-help-id="ai_action_plans.plan_button" onClick={() => void createPlan()} disabled={!!busyLabel || !prompt.trim() || (mode === 'shortlist_broadcast' && !jobId)} className="lg:h-[86px]">
              {busyLabel === 'Planning' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Plan
            </Button>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-5 xl:grid-cols-[280px_1fr]">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-glass-text-2">Recent plans</p>
          {loading ? (
            <div className="rounded-lg border border-glass bg-glass-2 p-4 text-sm text-glass-text-2">Loading plans...</div>
          ) : plans.length === 0 ? (
            <div className="rounded-lg border border-glass bg-glass-2 p-4 text-sm text-glass-text-2">No plans yet.</div>
          ) : (
            plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setActivePlan(plan)}
                className={`w-full cursor-pointer rounded-lg border p-3 text-left transition-colors ${activePlan?.id === plan.id ? 'border-accent2/40 bg-accent2/10' : 'border-glass bg-glass-2 hover:bg-glass-3'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="line-clamp-1 text-sm font-medium text-glass-text">{plan.title}</span>
                  <Badge variant={statusVariant(plan.status)}>{plan.status}</Badge>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-glass-text-2">{plan.summary}</p>
              </button>
            ))
          )}
        </div>

        {activePlan ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-glass bg-glass-2 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold tracking-tightest text-glass-text">{activePlan.title}</h2>
                    <Badge variant={statusVariant(activePlan.status)}>{activePlan.status}</Badge>
                    {activePlan.model && <Badge variant="outline">{activePlan.model}</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-glass-text-2">{activePlan.summary}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => void dryRun()} disabled={!!busyLabel}>
                    <ClipboardCheck className="mr-2 h-4 w-4" />
                    Dry run
                  </Button>
                  <Button variant="outline" onClick={() => void approveAll()} disabled={!!busyLabel}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Approve all
                  </Button>
                  <Button data-help-id="ai_action_plans.execute_button" onClick={() => void execute()} disabled={!!busyLabel || approvedCount === 0}>
                    {busyLabel === 'Executing' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                    Execute
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {activePlan.actions.map((action, index) => (
                <div key={action.id} className="rounded-lg border border-glass bg-glass-2 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-md border border-glass bg-glass-3 text-xs font-semibold text-glass-text-2">{index + 1}</span>
                        <h3 className="font-semibold text-glass-text">{action.title}</h3>
                        <Badge variant="outline">{action.type}</Badge>
                        <Badge variant={riskVariant(action.riskLevel)}>{action.riskLevel}</Badge>
                        <Badge variant={statusVariant(action.approvalStatus)}>{action.approvalStatus}</Badge>
                        <Badge variant={statusVariant(action.executionStatus)}>{action.executionStatus}</Badge>
                      </div>
                      <p className="text-sm text-glass-text-2">{action.description}</p>
                      {action.editableFields.length > 0 && (
                        <p className="text-xs text-glass-text-3">Editable: {action.editableFields.join(', ')}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => void saveAction(action)} disabled={!!busyLabel || action.executionStatus === 'DONE'}>
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void rejectAction(action.id)} disabled={!!busyLabel || action.executionStatus === 'DONE'}>
                        <XCircle className="mr-2 h-4 w-4" />
                        Skip
                      </Button>
                    </div>
                  </div>

                  <textarea
                    value={editBuffers[action.id] ?? jsonText(action.params)}
                    onChange={(e) => setEditBuffers((prev) => ({ ...prev, [action.id]: e.target.value }))}
                    rows={8}
                    className="mt-3 w-full rounded-lg border border-glass bg-black/[0.03] px-3 py-2 font-mono text-xs text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40 dark:bg-black/20"
                    disabled={action.executionStatus === 'DONE'}
                  />

                  {editErrors[action.id] && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{editErrors[action.id]}</p>}
                  {action.dryRunResult && (
                    <div className="mt-3 rounded-lg border border-glass bg-glass-1 p-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant={action.dryRunResult.ok ? 'success' : 'destructive'}>{action.dryRunResult.ok ? 'DRY RUN OK' : 'DRY RUN FAILED'}</Badge>
                      </div>
                      {action.dryRunResult.warnings?.map((warning) => <p key={warning} className="mt-2 text-amber-600 dark:text-amber-300">{warning}</p>)}
                      {action.dryRunResult.errors?.map((err) => <p key={err} className="mt-2 text-red-600 dark:text-red-300">{err}</p>)}
                    </div>
                  )}
                  {action.executionResult && (
                    <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-300">
                      Created/updated {String(action.executionResult.entityType ?? 'record')}: {String(action.executionResult.label ?? action.executionResult.entityId ?? '')}
                    </p>
                  )}
                  {action.errorMessage && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{action.errorMessage}</p>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-glass bg-glass-2 p-8 text-center text-glass-text-2">
            Create or select an action plan to review proposed work.
          </div>
        )}
      </div>
    </div>
  );
}
